import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    Paper,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { ContentCopyRounded, DocumentScannerRounded } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import server from '../../../services/server';
import useErrors from '../../../hooks/useErrors';
import DocumentInputMasked from '../../../components/DocumentInputMasked';
import PhoneInputMasked from '../../../components/PhoneInputMasked';
import ZipCodeInputMasked from '../../../components/ZipCodeInputMasked';
import CreditCardInputMasked from '../../../components/CreditCardInputMasked';
import { PriceInputMasked } from '../../../components/PriceMask';
import { payment_status } from '../../../services/helpers';

const FAILED_RETRYABLE_STATUSES = new Set([
    'REPROVED_BY_RISK_ANALYSIS',
    'CREDIT_CARD_CAPTURE_REFUSED',
    'ATTEMP',
]);

const AWAITING_PAYMENT_STATUSES = new Set(['PENDING', 'WAITING']);

function isAwaitingBoletoOrPix(paymentStatus, paymentData) {
    if (!paymentData || !AWAITING_PAYMENT_STATUSES.has(paymentStatus)) return false;
    const type = paymentData.billingType;
    return (
        type === 'BOLETO' ||
        type === 'PIX' ||
        Boolean(paymentData.bankSlipUrl) ||
        Boolean(paymentData.invoiceUrl) ||
        Boolean(paymentData.chargePix)
    );
}

const paymentSchema = Yup.object().shape({
    billingType: Yup.string().required(),
    name: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.required('Nome é obrigatório'),
    }),
    email: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.email('E-mail inválido').required('E-mail é obrigatório'),
    }),
    cpfCnpj: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.required('CPF/CNPJ é obrigatório'),
    }),
    phone: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.required('Telefone é obrigatório'),
    }),
    mobilePhone: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.required('Celular é obrigatório'),
    }),
    postalCode: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.required('CEP é obrigatório'),
    }),
    address: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.required('Endereço é obrigatório'),
    }),
    addressNumber: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.required('Número é obrigatório'),
    }),
    province: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.required('Bairro é obrigatório'),
    }),
    creditCard: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.required('Número do cartão é obrigatório'),
    }),
    creditCardHolderName: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.required('Nome no cartão é obrigatório'),
    }),
    creditCardExpiryMonth: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s =>
            s
                .required('Mês é obrigatório')
                .matches(/^(0[1-9]|1[0-2])$/, 'Mês inválido (MM)'),
    }),
    creditCardExpiryYear: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s =>
            s
                .required('Ano é obrigatório')
                .matches(/^(20)\d{2}$/, 'Ano inválido (AAAA)')
                .test('valid-year', 'Ano no passado', v => Number(v) >= new Date().getFullYear()),
    }),
    creditCardCcv: Yup.string().when('billingType', {
        is: 'CREDIT_CARD',
        then: s => s.required('CVV é obrigatório').matches(/^\d{3,4}$/, 'CVV inválido'),
    }),
});

export function PlanPaymentStep({ contract, mutate, planAmount }) {
    const { propostaId } = useParams();
    const { setErrors, enqueueSnackbar } = useErrors();

    const subscription = contract?.subscription;
    const paymentStatus = subscription?.paymentStatus ?? 'WAITING';
    const paymentData = subscription?.paymentData ?? null;

    const isPaid = ['CONFIRMED', 'RECEIVED', 'CONCLUDED'].includes(paymentStatus);
    const awaitingSlip = isAwaitingBoletoOrPix(paymentStatus, paymentData);
    // Formulário: ainda não gerou cobrança, ou falha de cartão (pode tentar de novo)
    const showPaymentForm =
        !isPaid &&
        !awaitingSlip &&
        (paymentStatus === 'WAITING' ||
            !paymentData ||
            FAILED_RETRYABLE_STATUSES.has(paymentStatus));
    const showFailedRetryHint = FAILED_RETRYABLE_STATUSES.has(paymentStatus);

    const {
        values,
        getFieldProps,
        setFieldValue,
        errors,
        handleSubmit,
        isSubmitting,
        isValid,
    } = useFormik({
        initialValues: {
            billingType: 'CREDIT_CARD',
            name: '',
            email: '',
            cpfCnpj: '',
            phone: '',
            mobilePhone: '',
            postalCode: '',
            address: '',
            addressNumber: '',
            addressComplement: '',
            province: '',
            creditCard: '',
            creditCardHolderName: '',
            creditCardExpiryMonth: '',
            creditCardExpiryYear: '',
            creditCardCcv: '',
        },
        validationSchema: paymentSchema,
        validateOnChange: true,
        onSubmit: async (vals, { setSubmitting }) => {
            try {
                setSubmitting(true);
                await server.put(`subscription/${propostaId}/payment`, vals);
                await mutate();
                enqueueSnackbar('Pagamento processado.');
            } catch (error) {
                setErrors(error);
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleFreePlan = async () => {
        try {
            await server.put(`subscription/${propostaId}/payment`, {});
            await mutate();
            enqueueSnackbar('Adesão gratuita confirmada.');
        } catch (error) {
            setErrors(error);
        }
    };

    const handleCopyPix = () => {
        const payload = paymentData?.chargePix?.payload;
        if (payload) {
            navigator.clipboard.writeText(payload).then(() => {
                enqueueSnackbar('Código PIX copiado.');
            });
        }
    };

    if (isPaid) {
        return (
            <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="h3" component="h2">
                            Pagamento do plano
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Cobrança da assinatura do plano Eurobem desta proposta (mensalidade / adesão
                            recorrente conforme o ciclo contratado).
                        </Typography>
                    </Box>
                    <Divider />
                    <Alert severity="success">
                        Mensalidade confirmada
                        {payment_status[paymentStatus] ? ` — ${payment_status[paymentStatus]}` : ''}.
                    </Alert>
                    {planAmount > 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            Valor do plano:{' '}
                            {Number(planAmount).toLocaleString('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                            })}
                        </Typography>
                    ) : null}
                </Stack>
            </Paper>
        );
    }

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
                <Box>
                    <Typography variant="h3" component="h2">
                        Pagamento do plano
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Cobrança da assinatura do plano Eurobem desta proposta (mensalidade / adesão
                        recorrente conforme o ciclo contratado).
                    </Typography>
                </Box>
                <Divider />

                <Typography variant="body1">
                    Para seguir com a proposta, conclua o pagamento do plano de assinatura.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    * A cobrança será faturada em nome da empresa contratante conforme indicado no contrato.
                </Typography>

                {/* Resumo do plano */}
                <Stack direction="row" spacing={2}>
                    {contract?.plan?.name && (
                        <TextField
                            label="Plano"
                            fullWidth
                            value={contract.plan.name}
                            InputProps={{ readOnly: true }}
                        />
                    )}
                    <TextField
                        label="Mensalidade"
                        fullWidth
                        value={planAmount === 0 ? 'Gratuito' : planAmount}
                        InputProps={{
                            readOnly: true,
                            ...(planAmount > 0 ? { inputComponent: PriceInputMasked } : {}),
                        }}
                    />
                </Stack>

                {/* Plano gratuito */}
                {planAmount === 0 && (
                    <Button variant="contained" onClick={handleFreePlan}>
                        Confirmar adesão gratuita
                    </Button>
                )}

                {/* Plano pago — formulário de pagamento */}
                {planAmount > 0 && showPaymentForm && (
                    <>
                        {showFailedRetryHint && (
                            <Alert severity="warning">
                                {payment_status[paymentStatus] ?? `Status: ${paymentStatus}`}. Você pode tentar
                                novamente.
                            </Alert>
                        )}

                        <ToggleButtonGroup
                            exclusive
                            value={values.billingType}
                            onChange={(_, v) => v && setFieldValue('billingType', v)}
                            fullWidth
                        >
                            <ToggleButton value="CREDIT_CARD">Cartão de Crédito</ToggleButton>
                            <ToggleButton value="BOLETO">Boleto</ToggleButton>
                            <ToggleButton value="PIX">PIX</ToggleButton>
                        </ToggleButtonGroup>

                        {values.billingType === 'CREDIT_CARD' && (
                            <Stack spacing={2}>
                                <Divider>Dados do titular do cartão</Divider>

                                <Stack direction="row" columnGap={2}>
                                    <TextField
                                        label="CPF/CNPJ"
                                        {...getFieldProps('cpfCnpj')}
                                        fullWidth
                                        error={!!errors.cpfCnpj}
                                        helperText={errors.cpfCnpj}
                                        InputProps={{ inputComponent: DocumentInputMasked }}
                                    />
                                    <TextField
                                        label="E-mail"
                                        {...getFieldProps('email')}
                                        fullWidth
                                        error={!!errors.email}
                                        helperText={errors.email}
                                    />
                                </Stack>
                                <TextField
                                    label="Nome completo"
                                    {...getFieldProps('name')}
                                    fullWidth
                                    error={!!errors.name}
                                    helperText={errors.name}
                                />
                                <Stack direction="row" columnGap={1}>
                                    <TextField
                                        label="Telefone"
                                        {...getFieldProps('phone')}
                                        fullWidth
                                        error={!!errors.phone}
                                        helperText={errors.phone}
                                        InputProps={{ inputComponent: PhoneInputMasked }}
                                    />
                                    <TextField
                                        label="Celular"
                                        {...getFieldProps('mobilePhone')}
                                        fullWidth
                                        error={!!errors.mobilePhone}
                                        helperText={errors.mobilePhone}
                                        InputProps={{ inputComponent: PhoneInputMasked }}
                                    />
                                </Stack>
                                <Stack direction="row" columnGap={2}>
                                    <TextField
                                        label="CEP"
                                        {...getFieldProps('postalCode')}
                                        fullWidth
                                        error={!!errors.postalCode}
                                        helperText={errors.postalCode}
                                        InputProps={{ inputComponent: ZipCodeInputMasked }}
                                    />
                                    <TextField
                                        label="Bairro"
                                        {...getFieldProps('province')}
                                        fullWidth
                                        error={!!errors.province}
                                        helperText={errors.province}
                                    />
                                </Stack>
                                <Stack direction="row" columnGap={2}>
                                    <TextField
                                        label="Endereço"
                                        {...getFieldProps('address')}
                                        fullWidth
                                        error={!!errors.address}
                                        helperText={errors.address}
                                    />
                                    <TextField
                                        sx={{ width: 200 }}
                                        label="Número"
                                        {...getFieldProps('addressNumber')}
                                        error={!!errors.addressNumber}
                                        helperText={errors.addressNumber}
                                    />
                                </Stack>
                                <TextField
                                    label="Complemento"
                                    {...getFieldProps('addressComplement')}
                                    fullWidth
                                />

                                <Divider>Dados do cartão</Divider>

                                <TextField
                                    label="Número do Cartão"
                                    {...getFieldProps('creditCard')}
                                    fullWidth
                                    error={!!errors.creditCard}
                                    helperText={errors.creditCard}
                                    InputProps={{ inputComponent: CreditCardInputMasked }}
                                />
                                <TextField
                                    label="Nome no Cartão"
                                    {...getFieldProps('creditCardHolderName')}
                                    fullWidth
                                    error={!!errors.creditCardHolderName}
                                    helperText={errors.creditCardHolderName}
                                />
                                <Stack direction="row" columnGap={2}>
                                    <TextField
                                        label="Mês de expiração (MM)"
                                        {...getFieldProps('creditCardExpiryMonth')}
                                        fullWidth
                                        error={!!errors.creditCardExpiryMonth}
                                        helperText={errors.creditCardExpiryMonth}
                                        inputProps={{ maxLength: 2 }}
                                    />
                                    <TextField
                                        label="Ano de expiração (AAAA)"
                                        {...getFieldProps('creditCardExpiryYear')}
                                        fullWidth
                                        error={!!errors.creditCardExpiryYear}
                                        helperText={errors.creditCardExpiryYear}
                                        inputProps={{ maxLength: 4 }}
                                    />
                                    <TextField
                                        label="CVV"
                                        {...getFieldProps('creditCardCcv')}
                                        sx={{ width: 150 }}
                                        error={!!errors.creditCardCcv}
                                        helperText={errors.creditCardCcv}
                                        inputProps={{ maxLength: 4 }}
                                    />
                                </Stack>

                                <Divider />
                                <Button
                                    variant="contained"
                                    sx={{ alignSelf: 'flex-end' }}
                                    onClick={handleSubmit}
                                    disabled={!isValid || isSubmitting}
                                    loading={isSubmitting}
                                >
                                    Processar pagamento
                                </Button>
                            </Stack>
                        )}

                        {(values.billingType === 'BOLETO' || values.billingType === 'PIX') && (
                            <>
                                <Divider />
                                <Button
                                    variant="contained"
                                    sx={{ alignSelf: 'flex-end' }}
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                    loading={isSubmitting}
                                >
                                    Gerar pagamento
                                </Button>
                            </>
                        )}
                    </>
                )}

                {/* Boleto/PIX gerado — aguardando compensação */}
                {planAmount > 0 && awaitingSlip && (
                    <>
                        <Alert severity="info">
                            Cobrança gerada. Assim que o pagamento for confirmado, a proposta segue automaticamente.
                        </Alert>
                        <Divider />
                        {(paymentData.billingType === 'BOLETO' || paymentData.invoiceUrl || paymentData.bankSlipUrl) && (
                            <Card>
                                <CardContent>
                                    <Stack
                                        direction="row"
                                        columnGap={2}
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <DocumentScannerRounded sx={{ fontSize: 56 }} color="action" />
                                        <Typography variant="body1" flex={1}>
                                            Seu boleto vence em até 5 dias úteis. Realize o pagamento para concluir a
                                            proposta.
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            color="success"
                                            href={paymentData.invoiceUrl || paymentData.bankSlipUrl}
                                            target="_blank"
                                            rel="noopener"
                                        >
                                            Pagar boleto
                                        </Button>
                                    </Stack>
                                </CardContent>
                            </Card>
                        )}

                        {(paymentData.billingType === 'PIX' || paymentData.chargePix) && (
                            <Stack spacing={2} alignItems="center">
                                {paymentData.chargePix?.encodedImage && (
                                    <Box
                                        component="img"
                                        src={`data:image/png;base64,${paymentData.chargePix.encodedImage}`}
                                        alt="QR Code PIX"
                                        width={200}
                                        height={200}
                                        border={1}
                                        borderColor="text.secondary"
                                        borderRadius={1}
                                    />
                                )}
                                <TextField
                                    fullWidth
                                    value={paymentData.chargePix?.payload ?? ''}
                                    label="Código PIX"
                                    InputProps={{ readOnly: true }}
                                />
                                <Button
                                    variant="outlined"
                                    startIcon={<ContentCopyRounded />}
                                    onClick={handleCopyPix}
                                >
                                    Copiar código PIX
                                </Button>
                            </Stack>
                        )}
                    </>
                )}

                {planAmount > 0 && !showPaymentForm && !awaitingSlip && !isPaid && (
                    <Alert severity="info">
                        {payment_status[paymentStatus] ?? `Status: ${paymentStatus}`}
                    </Alert>
                )}
            </Stack>
        </Paper>
    );
}
